import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFavorites } from '../services/favoritesService';
import { vesselService, CatchRecord } from '../services/api';

const formatNumberWithSpaces = (num: number): string => {
  return Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

function LatestCatches() {
  const [catches, setCatches] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadLatestCatches();
  }, []);

  const loadLatestCatches = async () => {
    const favorites = getFavorites();

    if (favorites.vessels.length === 0) {
      return; // No favorites, nothing to display
    }

    setLoading(true);
    try {
      // Fetch recent catches for all favorite vessels (last 30 days)
      const today = new Date().toISOString().split('T')[0];
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

      const allCatches: any[] = [];

      for (const vessel of favorites.vessels) {
        try {
          const result = await vesselService.getCatch(vessel.registreringsmerke, thirtyDaysAgo, today);
          if (result.daily && result.daily.length > 0) {
            allCatches.push(...result.daily);
          }
        } catch (error) {
          // Skip vessels with no data
          console.log(`No data for vessel ${vessel.registreringsmerke}`);
        }
      }

      // Group by dokumentnummer and aggregate totals + species breakdown
      const grouped = allCatches.reduce((acc: { [key: string]: any }, record: any) => {
        const docNum = record.dokumentnummer || 'unknown';
        if (!acc[docNum]) {
          acc[docNum] = {
            ...record,
            total_weight: 0,
            total_value: 0,
            species_breakdown: {},
          };
        }
        acc[docNum].total_weight += record.weight_kg || 0;
        acc[docNum].total_value += record.value_nok || 0;

        // Aggregate species weights
        const species = record.species || 'Unknown';
        acc[docNum].species_breakdown[species] = (acc[docNum].species_breakdown[species] || 0) + (record.weight_kg || 0);

        return acc;
      }, {});

      // Convert to array and sort by date (most recent first), take top 5 documents
      const sortedDocs: any[] = Object.values(grouped)
        .sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, 5);

      setCatches(sortedDocs);
    } catch (error) {
      console.error('Error loading latest catches:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCatchClick = (catchRecord: any) => {
    if (!catchRecord.registreringsmerke) return;

    navigate(`/vessel/${catchRecord.registreringsmerke}`, {
      state: {
        registreringsmerke: catchRecord.registreringsmerke,
        vessel_name: catchRecord.vessel_name,
        vessel_display: catchRecord.vessel_display,
        scrollToDocument: catchRecord.dokumentnummer,
      },
    });
  };

  const favorites = getFavorites();
  if (favorites.vessels.length === 0) {
    return null; // Don't show section if no favorites
  }

  return (
    <div className="card" style={{ marginTop: '30px' }}>
      <h3 style={{ marginBottom: '15px' }}>Siste leveringer fra favorittfartøy</h3>
      {loading ? (
        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
          Laster siste fangster...
        </div>
      ) : catches.length === 0 ? (
        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
          Ingen nylige fangster funnet for dine favorittfartøy (siste 30 dager)
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {catches.map((catchRecord, idx) => (
            <div
              key={`${catchRecord.dokumentnummer}-${idx}`}
              onClick={() => handleCatchClick(catchRecord)}
              style={{
                padding: '12px 15px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                backgroundColor: '#f9f9f9',
                display: 'grid',
                gridTemplateColumns: '80px 1fr auto',
                gap: '15px',
                alignItems: 'center',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = '#f0f0f0';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = '#f9f9f9';
              }}
            >
              <div>
                <div style={{ fontWeight: '600', fontSize: '0.9em', color: '#555', marginBottom: '2px' }}>
                  {catchRecord.date}
                </div>
                <div style={{ fontSize: '0.8em', color: '#999' }}>
                  {catchRecord.landing_municipality || 'N/A'}
                </div>
              </div>
              <div>
                <div style={{ fontWeight: '600', fontSize: '0.95em', marginBottom: '2px' }}>
                  {catchRecord.vessel_display || catchRecord.vessel_name || 'Unknown'}
                </div>
                <div style={{ fontSize: '0.85em', color: '#666' }}>
                  {(() => {
                    const speciesBreakdown = (catchRecord as any).species_breakdown || {};
                    const topSpecies = Object.entries(speciesBreakdown)
                      .sort(([, a]: any, [, b]: any) => b - a)
                      .slice(0, 3)
                      .map(([species]) => species)
                      .join(', ');
                    return topSpecies || 'N/A';
                  })()}
                </div>
              </div>
              <div style={{ textAlign: 'right', fontSize: '0.9em', color: '#555' }}>
                {formatNumberWithSpaces((catchRecord as any).total_weight || 0)} kg
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default LatestCatches;
